import { useCallback, useRef } from 'react';
import { useApi } from '@hero/context';
import type { AgentSSEEvent, AgentSendMessageAPI } from '@hero/api';
import { useMutationAPI, useQueryAPI } from '../../utils';
import type {
  UseAgentConversationListApi,
  UseAgentMediaUrlApi,
  UseAgentTranscriptApi,
} from './types';

/**
 * Imperative streamer for `POST /agent/messages`.
 *
 * `sendMessage` is fundamentally streaming — TanStack Query's
 * `useMutation` / `useQuery` aren't the right fit. We expose a tiny
 * imperative hook that returns a `start(body)` function. The caller
 * supplies an `onEvent` callback that runs for each SSE event; we
 * keep an internal `AbortController` so callers can cancel mid-turn
 * (`stop()`).
 *
 * Cache invalidation lives on `useAgentConfirmMessageApi` — once an
 * action lands (order created, client created, etc.) we bust the
 * relevant entity caches so the rest of the app re-renders.
 */
export interface UseAgentSendMessageStream {
  start: (body: AgentSendMessageAPI['req'], onEvent: (event: AgentSSEEvent) => void) => Promise<void>;
  stop: () => void;
}

export const useAgentSendMessageStream = (): UseAgentSendMessageStream => {
  const api = useApi();
  const abortRef = useRef<AbortController | null>(null);

  const start = useCallback(
    async (
      body: AgentSendMessageAPI['req'],
      onEvent: (event: AgentSSEEvent) => void,
    ) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const stream = (
        api.agent.sendMessage as (
          body: AgentSendMessageAPI['req'],
          options?: { signal?: AbortSignal },
        ) => AsyncGenerator<AgentSSEEvent, void, void>
      )(body, { signal: controller.signal });

      try {
        for await (const event of stream) {
          if (controller.signal.aborted) break;
          onEvent(event);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          throw err;
        }
      }
    },
    [api],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  return { start, stop };
};

/**
 * Confirm / decline a staged action.
 *
 * Wraps `useMutationAPI` so the agent entity cache (and any related
 * entity — orders, clients, saved-locations, drivers — bust naturally
 * via the entity invalidation chains).
 */
export const useAgentConfirmMessageApi = (
  apiConfig?: Parameters<typeof useMutationAPI>[0]['apiConfig'],
) => {
  const api = useApi();
  return useMutationAPI({
    entity: 'agent',
    apiFn: api.agent.confirmMessage,
    apiConfig,
  });
};

/**
 * The caller's conversation threads, newest activity first.
 *
 * API comes from ApiProvider (`useApi()` — common on every SDK); pass
 * `apiConfig` to override `apiFn` or disable the query.
 *
 * @see Hook API: {@link UseAgentConversationListApi}
 */
export const useAgentConversationListApi: UseAgentConversationListApi = (
  query,
  apiConfig,
) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'agent',
    apiFn: api.agent.listConversations,
    query,
    apiConfig,
  });
};

/**
 * Full transcript of one thread, oldest message first. The chat panel
 * reads it on open to find confirmation cards still awaiting the user,
 * which a page reload would otherwise leave stranded server-side.
 *
 * API comes from ApiProvider (`useApi()` — common on every SDK); pass
 * `apiConfig` to override `apiFn` or disable the query.
 *
 * @see Hook API: {@link UseAgentTranscriptApi}
 */
export const useAgentTranscriptApi: UseAgentTranscriptApi = (query, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'agent',
    apiFn: api.agent.getTranscript,
    query,
    apiConfig,
  });
};

/**
 * Playable URL for a voice clip held in private storage.
 *
 * Resolve this when the player mounts, not alongside the transcript:
 * the URL is signed and expires, so a long-cached one hands the user a
 * dead `<audio>` element.
 *
 * API comes from ApiProvider (`useApi()` — common on every SDK); pass
 * `apiConfig` to override `apiFn` or disable the query.
 *
 * @see Hook API: {@link UseAgentMediaUrlApi}
 */
export const useAgentMediaUrlApi: UseAgentMediaUrlApi = (query, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'agent',
    apiFn: api.agent.getMediaUrl,
    query,
    apiConfig,
  });
};
